// src/dust/index.ts
var BARE_TRANSFER_PROTOCOLS = /* @__PURE__ */ new Set([
  "native_transfer",
  "erc20",
  "erc721",
  "erc1155"
]);
var FALLBACK_PROTOCOL_ID = "generic";
function claimedProtocols(row) {
  const primary = row.primaryProtocolId || "";
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  for (const id of [primary, ...row.protocolIds ?? []]) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    if (id === FALLBACK_PROTOCOL_ID && id !== primary) continue;
    out.push(id);
  }
  return out;
}
var DEFAULT_DUST_USD = 0.01;

// src/poisoning/index.ts
var MIN_SHARED_PREFIX = 3;
var MIN_SHARED_SUFFIX = 4;
var EVM_ADDRESS = /^0x[0-9a-f]{40}$/;
function normalise(address) {
  const a = (address ?? "").toLowerCase();
  return EVM_ADDRESS.test(a) ? a : null;
}
function sharedAffixes(a, b) {
  const x = normalise(a);
  const y = normalise(b);
  if (!x || !y) return null;
  const bx = x.slice(2);
  const by = y.slice(2);
  let prefix = 0;
  while (prefix < bx.length && bx[prefix] === by[prefix]) prefix += 1;
  let suffix = 0;
  while (suffix < bx.length && bx[bx.length - 1 - suffix] === by[by.length - 1 - suffix]) suffix += 1;
  return { prefix, suffix };
}
function isLookalike(a, b) {
  const shared = sharedAffixes(a, b);
  if (!shared) return false;
  if (shared.prefix === 40) return false;
  return shared.prefix >= MIN_SHARED_PREFIX && shared.suffix >= MIN_SHARED_SUFFIX;
}
function deriveImpersonations(rows, read, subjects) {
  const subjectSet = /* @__PURE__ */ new Set();
  for (const s of subjects) {
    const a = normalise(s);
    if (a) subjectSet.add(a);
  }
  if (subjectSet.size === 0) return /* @__PURE__ */ new Map();
  const genuine = /* @__PURE__ */ new Set();
  const unprovenSenders = /* @__PURE__ */ new Set();
  const realSenders = /* @__PURE__ */ new Set();
  for (const row of rows) {
    const candidate = read(row);
    const from = normalise(candidate.fromAddress);
    const to = normalise(candidate.toAddress);
    if (!from || !to || from === to) continue;
    if (subjectSet.has(from) && !subjectSet.has(to)) {
      genuine.add(to);
      continue;
    }
    if (subjectSet.has(to) && !subjectSet.has(from)) {
      const usd = candidate.valueUsd;
      if (typeof usd === "number" && Number.isFinite(usd) && usd >= DEFAULT_DUST_USD) {
        genuine.add(from);
        realSenders.add(from);
      } else {
        unprovenSenders.add(from);
      }
    }
  }
  const out = /* @__PURE__ */ new Map();
  for (const sender of unprovenSenders) {
    if (realSenders.has(sender) || subjectSet.has(sender)) continue;
    let best = null;
    for (const target of genuine) {
      if (target === sender || !isLookalike(sender, target)) continue;
      const shared = sharedAffixes(sender, target);
      if (!best || shared.prefix + shared.suffix > best.prefix + best.suffix) {
        best = { sender, mimics: target, ...shared };
      }
    }
    if (best) out.set(sender, best);
  }
  return out;
}
function short(address, prefix, suffix) {
  const head = Math.max(4, prefix);
  const tail = Math.max(4, suffix);
  return `0x${address.slice(2, 2 + head)}\u2026${address.slice(-tail)}`;
}
function namesNothingBeyondATransfer(row) {
  const claimed = claimedProtocols(row);
  return claimed.length > 0 && claimed.every((id) => id === FALLBACK_PROTOCOL_ID || BARE_TRANSFER_PROTOCOLS.has(id));
}
function poisoningReason(row, impersonations) {
  const sender = normalise(row.fromAddress);
  if (!sender) return null;
  const hit = impersonations.get(sender);
  if (!hit) return null;
  const usd = row.valueUsd;
  if (typeof usd === "number" && Number.isFinite(usd) && usd >= DEFAULT_DUST_USD) return null;
  if (!namesNothingBeyondATransfer(row)) return null;
  const look = short(hit.sender, hit.prefix, hit.suffix);
  const real = short(hit.mimics, hit.prefix, hit.suffix);
  return `Sent from ${look}, a lookalike of ${real} \u2014 an address this history genuinely transacted with (${hit.prefix} leading and ${hit.suffix} trailing characters match). Address poisoning: the transfer plants the lookalike so a later copy-paste pays it.`;
}

export { MIN_SHARED_PREFIX, MIN_SHARED_SUFFIX, deriveImpersonations, isLookalike, poisoningReason, sharedAffixes };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map